import { useEffect, useState, useCallback } from "react";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Broadcast, Plus, Trash, Globe, Check, X, Copy, ArrowSquareOut, Warning,
} from "@phosphor-icons/react";

const TYPES = ["disaster", "news", "conservation", "other"];
const TYPE_COLOR = {
  disaster: "bg-red-50 text-red-700 border-red-200",
  news: "bg-blue-50 text-blue-700 border-blue-200",
  conservation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  other: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function CreateForm({ onCreated }) {
  const [f, setF] = useState({ title: "", type: "news", country: "", city: "", start_date: "", severity: "", description: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/event-sets", { ...f, title: f.title.trim() });
      toast.success(`Created ${data.evt_id}`);
      setF({ title: "", type: "news", country: "", city: "", start_date: "", severity: "", description: "" });
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="event-create-form">
      <p className="flex items-center gap-2 font-display text-base font-black"><Plus size={16} weight="bold" className="text-[#F27A54]" /> New event set</p>
      <div className="mt-3 space-y-2.5">
        <Input data-testid="event-title" value={f.title} onChange={set("title")} placeholder="Event title (e.g. Kerala Floods 2026)" className="h-10" />
        <div className="grid grid-cols-2 gap-2.5">
          <select data-testid="event-type" value={f.type} onChange={set("type")}
            className="h-10 rounded-md border border-border bg-background px-3 text-sm capitalize">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input data-testid="event-severity" value={f.severity} onChange={set("severity")} placeholder="Severity (optional)" className="h-10" />
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Input data-testid="event-country" value={f.country} onChange={set("country")} placeholder="Country" className="h-10" />
          <Input data-testid="event-city" value={f.city} onChange={set("city")} placeholder="City (optional)" className="h-10" />
        </div>
        <Input data-testid="event-date" type="date" value={f.start_date} onChange={set("start_date")} className="h-10" />
        <Textarea data-testid="event-desc" value={f.description} onChange={set("description")} placeholder="Short description" className="min-h-[70px]" />
        <Button data-testid="event-create-btn" onClick={submit} disabled={busy} className="w-full bg-[#F27A54] hover:bg-[#d9633f]">
          {busy ? "Creating…" : "Create event set"}
        </Button>
      </div>
    </div>
  );
}

function PhotoPicker({ eid, existing, onAdded }) {
  const [pool, setPool] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/public/gallery?limit=48").then(({ data }) => setPool(data.photos || [])).catch(() => {});
  }, []);
  const toggle = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const add = async () => {
    if (!chosen.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/event-sets/${eid}/photos`, { photo_ids: chosen });
      toast.success(`Added ${chosen.length} photo(s)`);
      setSel({});
      onAdded(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const already = new Set(existing || []);
  return (
    <div data-testid="event-photo-picker">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-zinc-700">Add photos from the public library</p>
        <Button data-testid="event-add-photos-btn" size="sm" onClick={add} disabled={!chosen.length || busy}
          className="bg-[#2D6A4F] hover:bg-[#245740]">Add {chosen.length || ""}</Button>
      </div>
      <div className="mt-3 grid max-h-[340px] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
        {pool.map((m) => {
          const on = !!sel[m.id]; const dis = already.has(m.id);
          return (
            <button key={m.id} type="button" disabled={dis} onClick={() => toggle(m.id)}
              data-testid={`event-pick-${m.id}`}
              className={`relative aspect-square overflow-hidden rounded-lg border-2 ${on ? "border-[#F27A54]" : "border-transparent"} ${dis ? "opacity-40" : ""}`}>
              <img src={assetUrl(m.image_url)} alt="" className="h-full w-full object-cover" />
              {on && <span className="absolute right-1 top-1 rounded-full bg-[#F27A54] p-0.5 text-white"><Check size={12} weight="bold" /></span>}
              {dis && <span className="absolute inset-0 grid place-items-center bg-black/40 text-[10px] font-bold text-white">Added</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventDetail({ event, onChange, onDeleted }) {
  const [e, setE] = useState(event);
  useEffect(() => { setE(event); }, [event]);
  const refresh = (data) => { setE(data); onChange(data); };

  const publish = async (publish) => {
    try {
      const { data } = await api.post(`/event-sets/${e.id}/publish`, { publish });
      toast.success(publish ? "Published" : "Unpublished");
      refresh(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const removePhoto = async (pid) => {
    try {
      const { data } = await api.delete(`/event-sets/${e.id}/photos/${pid}`);
      refresh(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this event set?")) return;
    try { await api.delete(`/event-sets/${e.id}`); toast.success("Deleted"); onDeleted(e.id); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const copyPull = () => {
    navigator.clipboard.writeText(`${window.location.origin}/api/public/event-sets/${e.evt_id}`);
    toast.success("Pull URL copied");
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="event-detail">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`border capitalize ${TYPE_COLOR[e.type] || TYPE_COLOR.other}`}>{e.type}</Badge>
            {e.severity && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{e.severity}</Badge>}
            {e.is_published
              ? <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="event-status-pub">Published</Badge>
              : <Badge variant="outline" data-testid="event-status-draft">Draft</Badge>}
          </div>
          <h2 className="mt-2 font-display text-xl font-black tracking-tight" data-testid="event-detail-title">{e.title}</h2>
          <p className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-mono">{e.evt_id}</span>
            {(e.location?.city || e.location?.country) && <span className="flex items-center gap-1"><Globe size={12} /> {[e.location.city, e.location.country].filter(Boolean).join(", ")}</span>}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {e.is_published
            ? <Button data-testid="event-unpublish-btn" size="sm" variant="outline" onClick={() => publish(false)}>Unpublish</Button>
            : <Button data-testid="event-publish-btn" size="sm" onClick={() => publish(true)} className="bg-[#F27A54] hover:bg-[#d9633f]">Publish</Button>}
          <Button data-testid="event-delete-btn" size="sm" variant="outline" onClick={del} className="text-red-600 hover:bg-red-50"><Trash size={15} /></Button>
        </div>
      </div>

      {e.description && <p className="mt-3 text-sm leading-relaxed text-zinc-700">{e.description}</p>}

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-[#FBFAF8] p-3 text-xs">
        <button onClick={copyPull} data-testid="event-copy-pull" className="flex items-center gap-1.5 font-semibold text-zinc-700 hover:text-[#F27A54]">
          <Copy size={13} /> Copy pull endpoint
        </button>
        {e.is_published && (
          <a href={`/event/${e.evt_id}`} target="_blank" rel="noreferrer" data-testid="event-view-public" className="flex items-center gap-1.5 font-semibold text-zinc-700 hover:text-[#F27A54]">
            <ArrowSquareOut size={13} /> View public page
          </a>
        )}
        <span className="text-muted-foreground">{e.photo_count} photo{e.photo_count === 1 ? "" : "s"}</span>
      </div>

      {/* Assigned photos */}
      <div className="mt-5">
        <p className="text-sm font-bold text-zinc-700">Photos in this event</p>
        {(!e.photos || e.photos.length === 0) ? (
          <p className="mt-2 flex items-center gap-1.5 rounded-lg border border-dashed border-border py-6 text-center text-xs text-muted-foreground">
            <Warning size={14} className="mx-auto" /> No photos yet — add some below, then publish.
          </p>
        ) : (
          <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="event-assigned">
            {e.photos.map((m) => (
              <div key={m.id} className="group relative aspect-square overflow-hidden rounded-lg border border-border">
                <img src={assetUrl(m.image_url)} alt={m.title || ""} className="h-full w-full object-cover" />
                <button onClick={() => removePhoto(m.id)} data-testid={`event-remove-${m.id}`}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 border-t border-border pt-4">
        <PhotoPicker eid={e.id} existing={(e.photos || []).map((p) => p.id)} onAdded={refresh} />
      </div>
    </div>
  );
}

export default function EventSets() {
  const [events, setEvents] = useState([]);
  const [selId, setSelId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/event-sets"); setEvents(data.events || []); }
    catch { /* likely non-admin */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!selId) { setDetail(null); return; }
    api.get(`/event-sets/${selId}`).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [selId]);

  const onCreated = (data) => { setEvents((p) => [{ ...data }, ...p]); setSelId(data.id); setDetail(data); };
  const onDetailChange = (data) => {
    setDetail(data);
    setEvents((p) => p.map((e) => (e.id === data.id ? { ...e, ...data } : e)));
  };
  const onDeleted = (id) => { setEvents((p) => p.filter((e) => e.id !== id)); setSelId(null); setDetail(null); };

  return (
    <div className="mx-auto max-w-6xl px-1 py-2" data-testid="event-sets-page">
      <div className="mb-5">
        <h1 className="flex items-center gap-2 font-display text-2xl font-black tracking-tight">
          <Broadcast size={24} weight="fill" className="text-[#F27A54]" /> Event Photo Sets
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Group photos into a canonical <span className="font-mono text-xs">PULSE-EVT-…</span> event that sister sites (e.g. WeatherPulse) pull with one API call.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
        <div className="space-y-4">
          <CreateForm onCreated={onCreated} />
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-2 text-sm font-bold text-zinc-700">All events</p>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-zinc-100" />)}</div>
            ) : events.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted-foreground" data-testid="event-empty">No event sets yet. Create one on the left.</p>
            ) : (
              <ul className="space-y-2" data-testid="event-list">
                {events.map((e) => (
                  <li key={e.id}>
                    <button onClick={() => setSelId(e.id)} data-testid={`event-item-${e.id}`}
                      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${selId === e.id ? "border-[#F27A54] bg-orange-50/50" : "border-border hover:border-[#F27A54]/40"}`}>
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-zinc-100">
                        {e.cover_image && <img src={assetUrl(e.cover_image)} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-bold">{e.title}</p>
                        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <span className="capitalize">{e.type}</span> · {e.photo_count} photo{e.photo_count === 1 ? "" : "s"}
                          {e.is_published ? <span className="text-emerald-600">· live</span> : <span>· draft</span>}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          {detail ? (
            <EventDetail event={detail} onChange={onDetailChange} onDeleted={onDeleted} />
          ) : (
            <div className="grid h-full min-h-[300px] place-items-center rounded-2xl border border-dashed border-border" data-testid="event-detail-empty">
              <p className="text-sm text-muted-foreground">Select an event to manage its photos, or create a new one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
